import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import {
  Bottom,
  BottomLoading,
  BoxLoading,
  Description,
  FirstLoading,
  FirstCard,
  FirstProjects,
  HeaderTask,
  Heading,
  Left,
  LeftLoading,
  Name,
  ProjectCard,
  RightLoading,
  Task,
  Tasklist,
  TaskText,
  TopLoading
} from './styles';
import Button from '../../../../component/button';
import Card from './card';
import { useHistory } from 'react-router-dom';
import Modal from '../../../../component/modals/modal';
import CreateTemplateModal from '../../../../component/modals/createTemplateModal';
import {
  deleteProjectTemplate,
  deleteTaskTemplate,
  getProjectTemplateList,
  getTaskTemplateList
} from '../../../../services/settingServices';
import { isEmpty } from '../../../../helpers/common';
import { captureException } from '../../../../services/logService';
import DeleteModal from '../../../../component/modals/deleteModal';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import { BUTTON_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, MENU_OPTIONS } from '../../../../global/constants';
import { ROUTES } from '../../../../routes/urls';
import { PROJECT_ANALYTICS, VIEW_ANALYTICS } from '../../../../global/analyticsConstants';
import { Dropdown, DropdownItem } from '../../../../component/dropdown';
import { rootStore } from '../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { ProjectTemplateInput } from '../../../../mobx/interfaces/project';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { Group, Header, PersonalText, Container } from '../../styles';
import IconButton from '../../../../component/button/iconButton';

const Templates = () => {
  // States
  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [openDeleteTemplateModal, setOpenDeleteTemplateModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isProjectTemplate, setIsProjectTemplate] = useState(false);

  //Mobx store variables
  const { projectTemplateStore, workspaceStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { projectTemplates: projectTemplateList, taskTemplateList } = projectTemplateStore;

  // Other variables
  const isMobile = useMobileDevice();
  const history = useHistory();
  const menuItems = [
    { key: 1, label: 'Edit' },
    { key: 2, label: 'Delete' }
  ];

  // Handles click event for creating a new template
  const onClickCreateTemplate = useCallback(() => {
    trackAnalyticActivity(PROJECT_ANALYTICS.TEMPLATE_CREATE_CLICKED);
    setOpenTemplateModal(true);
  }, []);

  // Closes the template creation modal
  const onCloseTemplateModal = useCallback(() => {
    setOpenTemplateModal(false);
  }, []);

  // Loads project and task template data from the server
  const loadData = useCallback(async () => {
    try {
      if (!isEmpty(workspace?.id)) {
        setLoading(true);
        await getProjectTemplateList(workspace?.id);
        await getTaskTemplateList();
      }
    } catch (error) {
      captureException(error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [workspace?.id]);

  useEffect(() => {
    trackAnalyticActivity(VIEW_ANALYTICS.SETTING_TEMPLATE);
    loadData();
  }, [loadData]);

  // Handles edit action for a template
  const onClickEdit = useCallback(
    async (itemId: string, isProject: boolean) => {
      if (isProject) {
        const templateDetails = projectTemplateList.find((template) => template.id === itemId);
        const projectTemplateInput: ProjectTemplateInput = {
          id: templateDetails?.id,
          name: templateDetails?.name,
          description: templateDetails?.description
        };
        projectTemplateStore.setProjectTemplateInput(projectTemplateInput);
        setOpenTemplateModal(true);
      } else {
        projectTemplateStore?.setCurrentTaskTemplate(itemId);
        history.push(ROUTES.TASK_TEMPLATES_SETTING_DETAILS.replace(':id', itemId));
      }
    },
    [history, projectTemplateList, projectTemplateStore]
  );

  // Handles delete action for a template
  const onClickDelete = useCallback((itemId: string) => {
    setSelectedItemId(itemId);
    setOpenDeleteTemplateModal(true);
  }, []);

  // Handles menu item click events (edit/delete)
  const handleClick = useCallback(
    async (key: number | string, itemId: string, isProject: boolean) => {
      key = Number(key);
      setIsProjectTemplate(isProject);
      if (key === MENU_OPTIONS.EDIT) {
        onClickEdit(itemId, isProject);
      } else {
        onClickDelete(itemId);
      }
    },
    [onClickDelete, onClickEdit]
  );

  // Closes the delete confirmation modal
  const onCloseDeleteModal = useCallback(() => {
    setSelectedItemId('');
    setOpenDeleteTemplateModal(false);
  }, []);

  // Deletes the selected template
  const deleteTemplate = useCallback(async () => {
    try {
      setButtonLoading(true);
      let result;
      if (isProjectTemplate) {
        result = await deleteProjectTemplate(selectedItemId);
      } else {
        result = await deleteTaskTemplate(selectedItemId);
      }
      if (result) {
        onCloseDeleteModal();
        loadData();
      }
    } catch (error) {
      console.log('error', error);
      setButtonLoading(false);
    } finally {
      setButtonLoading(false);
    }
  }, [isProjectTemplate, loadData, onCloseDeleteModal, selectedItemId]);

  // Navigates to task templates settings details page
  const handleOpenTemplate = () => {
    history.push(ROUTES.TASK_TEMPLATES_SETTING_DETAILS);
  };

  // Navigates to project templates settings details page
  const onClickProjectTemplate = (id?: string) => {
    if (!id) return;
    rootStore.projectTemplateStore.setCurrentProjectTemplate(id);
    history.push(ROUTES.TEMPLATES_SETTING_DETAILS.replace(':id', id));
  };

  // render more icon
  const renderMoreIcon = useCallback(
    (item: { id?: string }) => {
      return (
        <IconButton
          iconName='three-dots-horizontal-icon'
          size={isMobile ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.SMALL}
          variant={ICON_BUTTON_TYPE.TERTIARY}
          onClick={() => setSelectedItemId(item?.id || '')}
        />
      );
    },
    [isMobile]
  );

  // render plus icon
  const renderPlusIcon = useCallback((onClick: () => void) => {
    return (
      <IconButton
        iconName='plus-icon'
        size={ICON_BUTTON_SIZE.LARGE}
        variant={ICON_BUTTON_TYPE.TERTIARY}
        onClick={onClick}
      />
    );
  }, []);

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group>
            {!isMobile ? (
              <Header>
                <PersonalText>Templates</PersonalText>
              </Header>
            ) : (
              <ResponsiveHeader title='Templates' />
            )}
            <Bottom>
              <FirstCard>
                <HeaderTask>
                  <Heading>Project</Heading>
                  {projectTemplateList?.length > 0 &&
                    (!isMobile ? (
                      <Button
                        variant={BUTTON_TYPE.PRIMARY}
                        onClick={onClickCreateTemplate}
                        title='Create New'
                        buttonType='button'
                        size='small'
                      />
                    ) : (
                      renderPlusIcon(onClickCreateTemplate)
                    ))}
                </HeaderTask>
                {loading && projectTemplateList?.length === 0 && (
                  <BoxLoading>
                    <FirstLoading>
                      <LeftLoading>
                        <TopLoading className='loading-animation'></TopLoading>
                        <BottomLoading className='loading-animation'></BottomLoading>
                      </LeftLoading>
                      <RightLoading className='loading-animation'></RightLoading>
                    </FirstLoading>
                  </BoxLoading>
                )}
                {!loading && projectTemplateList?.length === 0 && (
                  <Card heading={'Create template for Project'} isProject={true} />
                )}
                {/* Project card */}
                {projectTemplateList?.length > 0 && (
                  <ProjectCard>
                    {projectTemplateList?.map((item, index: number) => {
                      return (
                        <FirstProjects key={item?.id}>
                          <Left onClick={() => onClickProjectTemplate(item?.id)}>
                            <Name>{item?.name}</Name>
                            {!isEmpty(item.description) && <Description>{item?.description}</Description>}
                          </Left>
                          <Dropdown
                            modalTitle='Manage Template'
                            activeClassName='active'
                            content={renderMoreIcon(item)}
                            trigger='click'>
                            {menuItems.map((item) => (
                              <DropdownItem
                                key={item.key}
                                label={item.label}
                                onClick={() => handleClick(item.key, selectedItemId, true)}
                                tone={item.label === 'Delete' ? 'critical' : 'primary'}
                              />
                            ))}
                          </Dropdown>
                        </FirstProjects>
                      );
                    })}
                  </ProjectCard>
                )}
              </FirstCard>
              <FirstCard>
                <HeaderTask>
                  <Heading>Task</Heading>
                  {taskTemplateList?.length > 0 &&
                    (!isMobile ? (
                      <Button
                        title='Create new'
                        variant={BUTTON_TYPE.PRIMARY}
                        size='small'
                        onClick={handleOpenTemplate}
                      />
                    ) : (
                      renderPlusIcon(handleOpenTemplate)
                    ))}
                </HeaderTask>

                {taskTemplateList?.length === 0 && (
                  <Card heading={'Create template for Task'} isTask={true} onClickTaskTemplate={handleOpenTemplate} />
                )}
                {/* Task list */}
                {taskTemplateList?.length > 0 && (
                  <Tasklist>
                    {taskTemplateList?.map((item, index) => {
                      return (
                        <Task key={item?.id}>
                          <TaskText>{item?.templateName}</TaskText>
                          <Dropdown
                            modalTitle='Manage Template'
                            activeClassName='active'
                            content={renderMoreIcon(item)}
                            trigger='click'>
                            {menuItems.map((item) => (
                              <DropdownItem
                                key={item.key}
                                label={item.label}
                                onClick={() => handleClick(item.key, selectedItemId, false)}
                                tone={item.label === 'Delete' ? 'critical' : 'primary'}
                              />
                            ))}
                          </Dropdown>
                        </Task>
                      );
                    })}
                  </Tasklist>
                )}
              </FirstCard>
            </Bottom>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation backNavigationPath={ROUTES.SETTING} />
      <Modal open={openTemplateModal} onClose={onCloseTemplateModal} maxWidth={'400px'}>
        <CreateTemplateModal onCancel={onCloseTemplateModal} />
      </Modal>
      <Modal open={openDeleteTemplateModal} onClose={onCloseDeleteModal} maxWidth={'334px'}>
        <DeleteModal
          onClose={onCloseDeleteModal}
          loading={buttonLoading}
          onOk={deleteTemplate}
          modalTitle='Delete Template?'
          description='Are you sure you want to delete this template?'
        />
      </Modal>
    </>
  );
};

export default observer(Templates);
